/**
 * The "Notes" section of a record slideout — the notes linked to this
 * contact / company / deal / meeting.
 *
 * Creation always goes through the shared compose panel (pre-linked to this
 * record); the section itself only lists, inline-edits and deletes. The tasks
 * section is its twin under the task family — keep the two in step.
 */

import { Plus } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useMemoCompose } from '@/contexts/MemoComposeContext';
import { useCurrentUserEntityId, useOrgMembers } from '@/hooks/useOrgMembers';
import { usePermissions } from '@/hooks/usePermissions';
import { QueryErrorState } from '@/lib/components';
import { useFocusedAssociationScroll } from '@/lib/crm/hooks/useFocusedAssociationScroll';

import { getMemoCreatorName, getMemoOwnerName } from '../memo/memoMembers';
import { type MemoPrivacy, memoPrivacy } from '../memo/notes.utils';
import {
  type Note,
  useMemoPrivacyToggle,
  useMemoRecords,
} from '../memo/useMemoRecords';
import { NoteCard } from '../notes/NoteCard';
import { useNoteTabActions } from '../notes/useNoteTabActions';
import { CollapsibleSection } from './collapsible-section';
import { SectionActionButton } from './section-action-button';

interface NotesSectionProps {
  orgId: number;
  entityId: string;
  entityTypeName: string;
  /** When set to true, triggers note creation from an external source. */
  createRequested?: boolean;
  /** Called after the create request has been handled so the parent can reset. */
  onCreateRequestHandled?: () => void;
}

const SKELETON_CARDS = [
  { titleW: 'w-1/3', lines: ['w-full', 'w-4/5'], metaW: 'w-2/5' },
  { titleW: 'w-2/5', lines: ['w-full', 'w-3/4'], metaW: 'w-1/3' },
];

function LoadingSkeleton() {
  return (
    <div className='space-y-4'>
      {SKELETON_CARDS.map((card, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key -- Skeleton loading cards; no item identity
          key={i}
          className='p-4 bg-[var(--color-bg-secondary)] border border-[var(--color-border-secondary)] rounded-xl'
        >
          <div
            className={`h-4 bg-[var(--color-bg-tertiary)] rounded animate-pulse ${card.titleW} mb-2`}
          />
          <div className='space-y-1.5'>
            {card.lines.map((w, j) => (
              <div
                // eslint-disable-next-line react/no-array-index-key -- Skeleton loading lines; no item identity
                key={j}
                className={`h-3.5 bg-[var(--color-bg-tertiary)] rounded animate-pulse ${w}`}
              />
            ))}
          </div>
          <div
            className={`h-3 bg-[var(--color-bg-tertiary)] rounded animate-pulse ${card.metaW} mt-3`}
          />
        </div>
      ))}
    </div>
  );
}

export function NotesSection({
  orgId,
  entityId,
  entityTypeName,
  createRequested,
  onCreateRequestHandled,
}: NotesSectionProps) {
  const { hasPermission } = usePermissions();
  const canDeleteNote = hasPermission('ahoy_note', 'ahoy_object_deleted');

  // "Add note" opens the shared composer pre-linked to this record — the same
  // hook the record's Notes tab uses, so both entry points open identically.
  const { startCreatingNote, canCreateNote } = useNoteTabActions(
    entityId,
    entityTypeName
  );
  const { editMemo } = useMemoCompose();

  const { user } = useAuthInfo();
  const userId = user?.id ? String(user.id) : undefined;
  const { members: orgUsers } = useOrgMembers(orgId);
  const userEntityId = useCurrentUserEntityId(orgId);

  const {
    records: notes,
    isLoading,
    error: fetchError,
    refetch,
    updateRecordAsync,
    isUpdating,
    updateError,
    deleteRecord,
    isDeleting,
    deleteError,
  } = useMemoRecords({
    orgId,
    entityId,
    entityTypeName,
    users: orgUsers,
  });
  const { togglePrivacy } = useMemoPrivacyToggle({
    orgId,
    recordType: 'ahoy_note',
    users: orgUsers,
  });

  // Scroll to and highlight a specific note when the URL carries the
  // generic `focusedAssociationId` param. Same hook NotesTab uses; works
  // because each NoteCard wrapper below stamps `data-focused-association-id`.
  useFocusedAssociationScroll(notes.length > 0);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Track which note an inline edit / delete targets so the shared mutation
  // state only surfaces on that card — otherwise a failed delete would render
  // its error on every note.
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  const handleEditNote = useCallback(
    (
      noteEntityId: string,
      title: string,
      content: string,
      opts?: {
        date?: string;
        ownerEntityId?: string;
        stageAssociation?: string | null;
        privacy?: MemoPrivacy;
      }
    ) => {
      const { date, ownerEntityId, stageAssociation, privacy } = opts ?? {};
      const note = notes.find(n => n.entity_id === noteEntityId);
      if (!note || !userId) {
        return;
      }
      setEditingNoteId(note.entity_id);
      // Sequence the edit before the privacy toggle: both advance the note's
      // seq, so running them concurrently can reject one with a stale seq.
      const runEdit = async () => {
        try {
          await updateRecordAsync({
            noteEntityId: note.entity_id,
            title,
            content,
            date,
            ownerEntityId,
            originalTitle: note.title,
            originalContent: note.content,
            originalDate: note.date,
            originalDatetime: note.datetime,
            originalOwnerEntityId: note.owner_entity_id,
            stageAssociation: stageAssociation ?? null,
            originalStageAssociation: note.stage_entity_id ?? null,
            lastEventSeq: note.last_event_seq,
            userId,
          });
        } catch {
          // Keep `editingNoteId` set so `editError` stays visible in the
          // card's edit form; skip the privacy toggle so a failed edit never
          // applies a partial change.
          return;
        }

        if (privacy && privacy !== memoPrivacy(note)) {
          const visibilitySaved = await togglePrivacy(note.entity_id, privacy);
          if (!visibilitySaved) {
            // Keep `editingNoteId` set so the edit stays open and retryable —
            // togglePrivacy already toasted + logged the rejection. Closing here
            // would read to the user as a successful save.
            return;
          }
        }

        setEditingNoteId(null);
      };
      void runEdit();
    },
    [updateRecordAsync, togglePrivacy, userId, notes]
  );

  const handleDeleteNote = useCallback(
    async (noteEntityId: string) => {
      const note = notes.find(n => n.entity_id === noteEntityId);
      if (!note || !userId) {
        return;
      }
      // Not caught on purpose: NoteCard's confirm dialog needs the rejection
      // to stay open and render `deleteError`. On success the note leaves
      // `notes` and the card unmounts; on failure `deletingNoteId` stays set
      // so the inline error stays anchored to the right card.
      setDeletingNoteId(noteEntityId);
      await deleteRecord({
        noteEntityId,
        lastEventSeq: note.last_event_seq ?? 0,
        userId,
      });
    },
    [deleteRecord, notes, userId]
  );

  // The pencil opens the compose panel on the note (the panel fetches
  // everything it needs from the entity id).
  const handleEditNoteClick = useCallback(
    (noteToEdit: Note) => {
      editMemo(noteToEdit.entity_id);
    },
    [editMemo]
  );

  // React to false→true transitions of the external create request only.
  const prevCreateRequested = useRef(false);
  useEffect(() => {
    if (createRequested && !prevCreateRequested.current) {
      if (canCreateNote && userEntityId) {
        startCreatingNote();
        onCreateRequestHandled?.();
        prevCreateRequested.current = true;
      }
      // If permissions aren't ready yet, don't update prevCreateRequested —
      // the effect will re-run when canCreateNote/userEntityId change.
    } else if (!createRequested) {
      prevCreateRequested.current = false;
    }
  }, [
    createRequested,
    canCreateNote,
    userEntityId,
    onCreateRequestHandled,
    startCreatingNote,
  ]);

  const actionButtons = useMemo(
    () =>
      canCreateNote && userEntityId ? (
        <SectionActionButton
          label='Add note'
          onClick={startCreatingNote}
          icon={Plus}
        />
      ) : undefined,
    [canCreateNote, userEntityId, startCreatingNote]
  );

  return (
    <CollapsibleSection title='Notes' actions={actionButtons}>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <div className='space-y-4'>
          {fetchError && (
            <QueryErrorState
              error={fetchError}
              label="We couldn't load notes"
              onRetry={handleRetry}
              size='sm'
            />
          )}

          {!fetchError && notes.length === 0 && (
            <p className='text-xs md:text-sm text-fg-secondary'>No notes yet</p>
          )}

          {!fetchError && notes.length > 0 && (
            <div className='space-y-4'>
              {notes.map((note: Note) => (
                <div
                  key={note.entity_id}
                  data-focused-association-id={note.entity_id}
                >
                  <NoteCard
                    note={note}
                    orgId={orgId}
                    ownerName={getMemoOwnerName(note, orgUsers)}
                    creatorName={getMemoCreatorName(note, orgUsers)}
                    users={orgUsers}
                    currentUserEntityId={userEntityId}
                    canDelete={canDeleteNote}
                    onEdit={handleEditNote}
                    onEditClick={handleEditNoteClick}
                    onDelete={handleDeleteNote}
                    isEditing={isUpdating && editingNoteId === note.entity_id}
                    isDeleting={isDeleting && deletingNoteId === note.entity_id}
                    editError={
                      editingNoteId === note.entity_id ? updateError : null
                    }
                    deleteError={
                      deletingNoteId === note.entity_id &&
                      deleteError instanceof Error
                        ? deleteError
                        : null
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}
